import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, Empty, Input, Modal, Select, message } from 'antd';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const GOLD = '#B8892A';
const TEAL = '#1A6B54';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';

const STATUS_KEYS = {
  upcoming: { key: 'exhibition.upcoming', color: '#E67E22' },
  live:     { key: 'exhibition.ongoing',  color: TEAL },
  ended:    { key: 'exhibition.ended',    color: '#95A5A6' },
};

const SIZE_KEYS = { standard: 'exhibition.standardBooth', premium: 'exhibition.premiumBooth', island: 'exhibition.islandBooth' };

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
}

function getCountdown(startDate, t) {
  if (!startDate) return null;
  const now = new Date();
  const start = new Date(startDate);
  const diff = start - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}${t('exhibition.days', '天')}${hours}${t('exhibition.hours', '小时')}`;
  if (hours > 0) return `${hours}${t('exhibition.hours', '小时')}`;
  return t('exhibition.upcoming');
}

export default function ExhibitionDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('booths'); // booths | items | map
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');

  // Apply booth modal
  const [showApply, setShowApply] = useState(false);
  const [applySize, setApplySize] = useState('standard');
  const [applyZone, setApplyZone] = useState('');
  const [applyNotes, setApplyNotes] = useState('');
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [merchantStatus, setMerchantStatus] = useState(null); // null | 'active' | 'none'

  useEffect(() => {
    api.get(`/exhibitions/${id}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (tab === 'items') {
      setItemsLoading(true);
      const params = {};
      if (search) params.search = search;
      api.get(`/exhibitions/${id}/items`, { params })
        .then(r => setItems(r.data?.data || []))
        .catch(() => {})
        .finally(() => setItemsLoading(false));
    }
  }, [tab, id, search]);

  // Check if current user is a certified merchant
  useEffect(() => {
    if (isLoggedIn) {
      api.get('/merchants/my').then(r => {
        setMerchantStatus(r.data?.status === 'active' ? 'active' : 'none');
      }).catch(() => setMerchantStatus('none'));
    }
  }, [isLoggedIn]);

  const handleApplyBooth = async () => {
    setApplySubmitting(true);
    try {
      await api.post(`/exhibitions/${id}/apply-booth`, {
        preferred_size: applySize,
        preferred_zone: applyZone || null,
        notes: applyNotes || null,
      });
      message.success(t('exhibition.applySuccess', '参展申请已提交，请等待审核'));
      setShowApply(false);
      setApplyNotes('');
    } catch (err) {
      message.error(err.response?.data?.error || t('exhibition.applyFailed', '申请失败'));
    } finally { setApplySubmitting(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  if (!data) return <div style={{ textAlign: 'center', padding: 80, color: MUTED }}>{t('exhibition.notFound', '展会不存在')}</div>;

  const ss = STATUS_KEYS[data.status] || STATUS_KEYS.ended;
  const countdown = data.status === 'upcoming' ? getCountdown(data.start_date, t) : null;
  const booths = data.booths || [];
  const zones = [...new Set(booths.map(b => b.zone).filter(Boolean))];
  const filteredBooths = zoneFilter ? booths.filter(b => b.zone === zoneFilter) : booths;

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 16px',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/exhibitions'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK }}>←</button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>{t('exhibition.detail')}</span>
          <div style={{ width: 30 }} />
        </div>

        {/* Banner */}
        <div style={{
          background: data.banner_url
            ? `url(${data.banner_url}) center/cover`
            : 'linear-gradient(135deg, #1A2E28, #2A4A3E)',
          height: 180, display: 'flex', alignItems: 'flex-end',
          padding: '20px 24px', position: 'relative',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(transparent 30%, rgba(0,0,0,.6) 100%)',
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{data.name}</span>
              <span style={{
                fontSize: 10, padding: '2px 6px', borderRadius: 3,
                background: 'rgba(255,255,255,.2)', color: ss.color,
              }}>{t(ss.key)}</span>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.7)' }}>
              📍 {data.venue || data.city || '—'} · 📅 {formatDate(data.start_date)} — {formatDate(data.end_date)}
            </div>
          </div>
        </div>

        {/* Countdown / Status Bar */}
        {countdown && (
          <div style={{
            background: 'linear-gradient(90deg, #FEF5E7, #FDEBD0)',
            padding: '10px 16px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8,
          }}>
            <span style={{ fontSize: 13, color: '#E67E22', fontWeight: 600 }}>
              {t('exhibition.countdownPrefix', '距开展还有')} {countdown}
            </span>
            <span style={{ fontSize: 11, color: '#E67E22', opacity: .7 }}>
              {t('exhibition.previewHint', '展品已上线，可提前浏览收藏')}
            </span>
          </div>
        )}
        {data.status === 'live' && (
          <div style={{
            background: 'linear-gradient(90deg, #E8F8F5, #D5F5E3)',
            padding: '10px 16px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8,
          }}>
            <span style={{ fontSize: 13, color: TEAL, fontWeight: 600 }}>
              {t('exhibition.inProgress', '展会进行中')}
            </span>
            <span style={{ fontSize: 11, color: TEAL, opacity: .7 }}>
              {t('exhibition.onlineOrderSupport', '线上同价 · 支持在线下单')}
            </span>
          </div>
        )}

        {/* Stats Row */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          {[
            { label: t('exhibition.booths', '展位'), value: booths.length },
            { label: t('exhibition.exhibits', '展品'), value: items.length || '—' },
            { label: t('exhibition.organizer', '主办方'), value: data.organizer || '—' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center', padding: '14px 0' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: INK }}>{s.value}</div>
              <div style={{ fontSize: 11, color: MUTED }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Description */}
        {data.description && (
          <div style={{
            margin: '12px 16px 0', padding: '14px 16px',
            background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
            fontSize: 13, color: MUTED, lineHeight: 1.7,
          }}>{data.description}</div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex', margin: '16px 16px 0',
          background: '#fff', borderRadius: 8, border: `1px solid ${BD}`, overflow: 'hidden',
        }}>
          {[
            { key: 'booths', label: t('exhibition.boothNav', '展位导航') },
            { key: 'items', label: t('exhibition.allExhibits', '全部展品') },
            ...(data.floor_plan_url ? [{ key: 'map', label: t('exhibition.venueMap', '场馆地图') }] : []),
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              flex: 1, padding: '12px 0', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? TEAL : MUTED,
              background: tab === t.key ? `${TEAL}08` : 'transparent',
              borderBottom: tab === t.key ? `2px solid ${TEAL}` : '2px solid transparent',
            }}>{t.label}</button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ margin: '12px 16px 0' }}>
          {tab === 'booths' && (
            <div>
              {/* Zone filter */}
              {zones.length > 1 && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                  <button onClick={() => setZoneFilter('')} style={{
                    padding: '4px 12px', borderRadius: 14, fontSize: 12, cursor: 'pointer',
                    border: !zoneFilter ? `1px solid ${TEAL}` : `1px solid ${BD}`,
                    background: !zoneFilter ? `${TEAL}10` : '#fff',
                    color: !zoneFilter ? TEAL : MUTED,
                  }}>{t('common.all')}</button>
                  {zones.map(z => (
                    <button key={z} onClick={() => setZoneFilter(z)} style={{
                      padding: '4px 12px', borderRadius: 14, fontSize: 12, cursor: 'pointer',
                      border: zoneFilter === z ? `1px solid ${TEAL}` : `1px solid ${BD}`,
                      background: zoneFilter === z ? `${TEAL}10` : '#fff',
                      color: zoneFilter === z ? TEAL : MUTED,
                    }}>{z}</button>
                  ))}
                </div>
              )}

              {filteredBooths.length === 0 ? <Empty description={t('exhibition.noBooths', '暂无展位')} /> :
                filteredBooths.map(booth => (
                  <button key={booth.id} onClick={() => navigate(`/exhibitions/${id}/booth/${booth.id}`)} style={{
                    width: '100%', textAlign: 'left', display: 'flex', gap: 14, padding: 14,
                    background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                    marginBottom: 10, cursor: 'pointer',
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: 8, flexShrink: 0,
                      background: booth.merchant_logo ? `url(${booth.merchant_logo}) center/cover` : '#F0EBE0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, color: GOLD, fontWeight: 600,
                    }}>{!booth.merchant_logo && booth.booth_no}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>
                          {booth.merchant_name || booth.booth_no}
                        </span>
                        <span style={{
                          fontSize: 10, padding: '2px 6px', borderRadius: 3,
                          background: `${TEAL}10`, color: TEAL,
                        }}>{booth.zone || booth.floor}</span>
                      </div>
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                        {t('exhibition.booth', '展位')} {booth.booth_no} · {t(SIZE_KEYS[booth.size_type] || 'exhibition.standardBooth', '标准展位')}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: GOLD }}>
                          {booth.item_count || 0} {t('exhibition.exhibits', '件展品')} ›
                        </span>
                        {booth.credit_score > 0 && (
                          <span style={{
                            fontSize: 10, padding: '1px 6px', borderRadius: 3,
                            background: booth.credit_tier === 'premium' ? '#F5EEF8'
                              : booth.credit_tier === 'excellent' ? '#EBF5FB'
                              : booth.credit_tier === 'good' ? '#EAFAF1' : '#F2F3F4',
                            color: booth.credit_tier === 'premium' ? '#9B59B6'
                              : booth.credit_tier === 'excellent' ? '#2980B9'
                              : booth.credit_tier === 'good' ? '#27AE60' : '#95A5A6',
                          }}>
                            {booth.credit_tier === 'premium' ? t('exhibition.creditPremium', '优质') : booth.credit_tier === 'excellent' ? t('exhibition.creditExcellent', '优秀') : booth.credit_tier === 'good' ? t('exhibition.creditGood', '良好') : ''} {Number(booth.credit_score).toFixed(0)}{t('exhibition.points', '分')}
                          </span>
                        )}
                        {booth.avg_rating > 0 && (
                          <span style={{ fontSize: 10, color: '#F39C12' }}>
                            ★{Number(booth.avg_rating).toFixed(1)} ({booth.review_count})
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              }
            </div>
          )}

          {tab === 'items' && (
            <div>
              <Input.Search
                placeholder={t('exhibition.searchPlaceholder', '搜索展品名称或品牌')}
                onSearch={setSearch}
                allowClear
                style={{ marginBottom: 12 }}
              />
              {itemsLoading ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> :
               items.length === 0 ? <Empty description={t('exhibition.noExhibits', '暂无展品')} /> :
               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                {items.map(item => {
                  const photos = item.photos || [];
                  const thumb = photos[0] || '';
                  const price = item.exhibition_price || item.price_cny;
                  return (
                    <button key={item.id} onClick={() => navigate(`/market/${item.id}`)} style={{
                      background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                      overflow: 'hidden', cursor: 'pointer', textAlign: 'left', padding: 0,
                    }}>
                      <div style={{
                        height: 140,
                        background: thumb ? `url(${thumb}) center/cover` : '#F5F5F5',
                      }} />
                      <div style={{ padding: '10px 12px' }}>
                        <div style={{
                          fontSize: 13, fontWeight: 500, color: INK,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{item.item_name}</div>
                        <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                          {item.brand} · {item.booth_no || '—'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                          <span style={{ fontSize: 15, fontWeight: 600, color: INK }}>
                            ¥{Number(price).toLocaleString()}
                          </span>
                          {item.exhibition_price && item.price_cny !== item.exhibition_price && (
                            <span style={{ fontSize: 11, color: '#CCC', textDecoration: 'line-through' }}>
                              ¥{Number(item.price_cny).toLocaleString()}
                            </span>
                          )}
                          {item.show_only === 1 && (
                            <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 2, background: '#F0F0F0', color: '#999' }}>{t('exhibition.showOnly', '仅展示')}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
               </div>
              }
            </div>
          )}

          {tab === 'map' && data.floor_plan_url && (
            <div style={{
              background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
              padding: 16, textAlign: 'center',
            }}>
              <img src={data.floor_plan_url} alt={t('exhibition.venueMap', '场馆平面图')} style={{
                maxWidth: '100%', borderRadius: 8,
              }} />
              <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
                {t('exhibition.clickBoothHint', '点击展位标记可查看展位详情')}
              </div>
            </div>
          )}
        </div>

        {/* Merchant Apply Booth Button */}
        {merchantStatus === 'active' && data.status !== 'ended' && data.status !== 'archived' && (
          <div style={{ margin: '16px 16px 0' }}>
            <button onClick={() => setShowApply(true)} style={{
              width: '100%', padding: '14px 0', borderRadius: 8,
              border: 'none', background: TEAL, color: '#fff',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>{t('exhibition.merchantApply', '商家申请参展')}</button>
          </div>
        )}
        {merchantStatus === 'none' && isLoggedIn && (
          <div style={{ margin: '16px 16px 0' }}>
            <button onClick={() => navigate('/merchant/apply')} style={{
              width: '100%', padding: '14px 0', borderRadius: 8,
              border: `1px solid ${BD}`, background: '#fff', color: MUTED,
              fontSize: 13, cursor: 'pointer',
            }}>{t('exhibition.becomeMerchant', '成为认证商家，即可申请参展')}</button>
          </div>
        )}

        {/* Apply Booth Modal */}
        <Modal
          open={showApply} onCancel={() => setShowApply(false)}
          onOk={handleApplyBooth} confirmLoading={applySubmitting}
          title={t('exhibition.applyTitle', '申请参展')} okText={t('exhibition.submitApply', '提交申请')} cancelText={t('common.cancel')}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: INK, marginBottom: 4 }}>
              {t('exhibition.exhibitionLabel', '展会')}: {data.name}
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>
              {data.venue} · {formatDate(data.start_date)} — {formatDate(data.end_date)}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('exhibition.preferredBoothType', '偏好展位类型')}</label>
            <Select value={applySize} onChange={setApplySize} style={{ width: '100%' }}>
              <Select.Option value="standard">{t('exhibition.standardBooth', '标准展位')}</Select.Option>
              <Select.Option value="premium">{t('exhibition.premiumBooth', '精品展位')}</Select.Option>
              <Select.Option value="island">{t('exhibition.islandBooth', '岛台展位')}</Select.Option>
            </Select>
          </div>
          {zones.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('exhibition.preferredZone', '偏好展区')}（{t('common.optional')}）</label>
              <Select value={applyZone} onChange={setApplyZone} style={{ width: '100%' }}
                allowClear placeholder={t('exhibition.noLimit', '不限')}>
                {zones.map(z => <Select.Option key={z} value={z}>{z}</Select.Option>)}
              </Select>
            </div>
          )}
          <div>
            <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('exhibition.notes', '备注')}（{t('common.optional')}）</label>
            <Input.TextArea value={applyNotes} onChange={e => setApplyNotes(e.target.value)}
              placeholder={t('exhibition.notesPlaceholder', '如需特殊需求请说明')} rows={3} />
          </div>
        </Modal>
      </div>
    </div>
  );
}
